import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress,
  Alert, IconButton, Tooltip, useTheme, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Download as DownloadIcon,
  DevicesOther as DevicesIcon,
} from '@mui/icons-material';
import { RootState, ClientAsset } from '../../types';
import {
  fetchClientAssets,
  importClientAssetsCsv,
  deleteClientAsset,
  clearClientAssets,
} from '../../store/slices/clientsSlice';
import { DataTable, Column } from '../common';

const CRITICALITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
};

const CSV_TEMPLATE =
  'hostname,ip_address,name,asset_type,criticality,department,owner,tags\n' +
  'server01,192.168.1.10,Domain Controller,server,critical,IT,admin@company.com,"domain-controller,windows"\n' +
  'workstation-02,10.0.0.45,Finance PC,workstation,high,Finance,,,\n';

interface Props {
  clientId: string;
}

const AssetManagementPanel: React.FC<Props> = ({ clientId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { assets, assetsLoading } = useSelector((state: RootState) => state.clients);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<{ inserted: number; updated: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    dispatch(fetchClientAssets(clientId) as any);
  }, [clientId, dispatch]);

  const runImport = async (file: File) => {
    setImportError(null);
    setImportResult(null);
    const result = await dispatch(importClientAssetsCsv({ clientId, file }) as any);
    if (result.error) {
      setImportError(result.payload || 'Import failed');
    } else {
      setImportResult(result.payload);
      dispatch(fetchClientAssets(clientId) as any);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) runImport(file);
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) runImport(file);
  }, [clientId]);

  const handleDelete = (assetId: string) => {
    dispatch(deleteClientAsset({ clientId, assetId }) as any);
  };

  const handleClearAll = async () => {
    await dispatch(clearClientAssets(clientId) as any);
    setConfirmClearOpen(false);
    dispatch(fetchClientAssets(clientId) as any);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assets_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: Column<ClientAsset>[] = [
    {
      key: 'hostname',
      header: 'Hostname',
      minWidth: 130,
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
          {row.hostname || '—'}
        </Typography>
      ),
    },
    {
      key: 'ip_address',
      header: 'IP Address',
      minWidth: 110,
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: theme.palette.text.secondary }}>
          {row.ip_address || '—'}
        </Typography>
      ),
    },
    {
      key: 'criticality',
      header: 'Criticality',
      minWidth: 100,
      align: 'center',
      render: (row) => {
        const color = CRITICALITY_COLORS[row.criticality] || theme.palette.text.secondary;
        return (
          <Chip
            label={row.criticality}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              backgroundColor: `${color}22`,
              color: color,
              border: `1px solid ${color}55`,
            }}
          />
        );
      },
    },
    {
      key: 'asset_type',
      header: 'Type',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {row.asset_type}
        </Typography>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      minWidth: 110,
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {row.department || '—'}
        </Typography>
      ),
    },
    {
      key: 'name',
      header: 'Name / Owner',
      minWidth: 140,
      render: (row) => (
        <Box>
          {row.name && (
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{row.name}</Typography>
          )}
          {row.owner && (
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>{row.owner}</Typography>
          )}
          {!row.name && !row.owner && (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'id',
      header: '',
      minWidth: 40,
      align: 'center',
      render: (row) => (
        <Tooltip title="Remove asset">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.error.main } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DevicesIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Asset Inventory
          </Typography>
          {assets.length > 0 && (
            <Chip
              label={assets.length}
              size="small"
              sx={{
                backgroundColor: theme.palette.background.tertiary,
                color: theme.palette.text.secondary,
                fontWeight: 700,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Download CSV template">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{ borderColor: theme.palette.border?.main || theme.palette.divider }}
            >
              Template
            </Button>
          </Tooltip>

          <Button
            size="small"
            variant="contained"
            startIcon={assetsLoading ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={assetsLoading}
          >
            Import CSV
          </Button>

          {assets.length > 0 && (
            <Tooltip title="Clear all assets">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setConfirmClearOpen(true)}
                sx={{ borderColor: theme.palette.error.main }}
              >
                Clear All
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Alerts */}
      {importResult && (
        <Alert severity="success" onClose={() => setImportResult(null)} sx={{ mb: 2 }}>
          Import complete — {importResult.inserted} added, {importResult.updated} updated.
        </Alert>
      )}
      {importError && (
        <Alert severity="error" onClose={() => setImportError(null)} sx={{ mb: 2 }}>
          {importError}
        </Alert>
      )}

      {/* Drop zone (visible when no assets) */}
      {assets.length === 0 && !assetsLoading && (
        <Paper
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.border?.main || theme.palette.divider}`,
            borderRadius: 2,
            p: 5,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s ease',
            backgroundColor: dragOver ? `${theme.palette.primary.main}08` : 'transparent',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Drop a CSV file here, or click to browse
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
            Columns: hostname, ip_address, name, asset_type, criticality, department, owner, tags
          </Typography>
        </Paper>
      )}

      {/* Table */}
      {(assets.length > 0 || assetsLoading) && (
        <DataTable<ClientAsset>
          columns={columns}
          data={assets}
          rowKey={(row) => row.id}
          isLoading={assetsLoading}
          emptyMessage="No assets found"
          emptySubMessage="Import a CSV file to populate the asset inventory"
          loadingMessage="Loading assets…"
          minTableWidth={600}
          rowHeight={52}
        />
      )}

      {/* Confirm clear dialog */}
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear all assets?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all {assets.length} assets for this client.
            You can re-import at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button onClick={handleClearAll} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetManagementPanel;
